import dummySportsEvent from '@/features/market/data/dummySportsEvent.json'
import { QUESTION_STATUS } from '@/constants/marketBackendEnums'
import { i18n } from '@/locale/i18n'

// Set to false when the API is ready.
export const USE_DUMMY_DATA = false

const FINISHED_MATCH_STATUSES = new Set(['FT', 'AET', 'PEN', 'AWD', 'WO'])

const INPLAY_MATCH_STATUSES = new Set([
  'INPLAY_1ST_HALF',
  '1ST_HALF',
  'INPLAY_2ND_HALF',
  '2ND_HALF',
  'HT',
  'HALF_TIME',
  'BREAK',
  'ET',
  'INPLAY_ET',
  'INPLAY_ET_2ND_HALF',
  'ET_BREAK',
  'EXTRA_TIME_BREAK',
  'INPLAY_PENALTIES',
  'PEN_LIVE',
  'PEN_BREAK',
])

export const isSportsMatchFinished = sportsData => {
  // return true;
  if (!sportsData) return false
  if (sportsData.final_type === 'finished') return true
  const status = sportsData.source_status || sportsData.match_status
  return FINISHED_MATCH_STATUSES.has(status)
}

/**
 * In-play detection from the feed status code. The question-level is_live
 * flag can lag behind the feed, so status codes are the reliable signal.
 */
export const isSportsMatchInPlay = sportsData => { 
  if (!sportsData) return false
  const status = sportsData.source_status
  if (!status) return false
  return INPLAY_MATCH_STATUSES.has(String(status).toUpperCase())
}

/** Whether the match is currently being played (backend flag or feed status). */
export const isSportsMatchLive = question =>
  Boolean(question?.is_live) || isSportsMatchInPlay(question?.event_extra_data)

/**
 * Regular-time score. Soccer feeds split it by phase (`*_score_regular_time`); the other
 * sports carry one running `home_score` / `away_score`, which stands in when the phase
 * fields are absent.
 */
const getRegularTimeScores = sportsData => {
  if (sportsData?.home_score_regular_time != null || sportsData?.away_score_regular_time != null) {
    return { home: sportsData.home_score_regular_time, away: sportsData.away_score_regular_time }
  }
  return { home: sportsData?.home_score ?? null, away: sportsData?.away_score ?? null }
}

export const shouldShowSportsScore = (question, sportsData) => {
  const { home, away } = getRegularTimeScores(sportsData)
  if (home == null && away == null) return false
  /* A resolved market's score is final whether or not the feed sent a finished status. */
  return isSportsMatchLive(question) || isSportsMatchFinished(sportsData) || isSportsMarketResolved(question)
}

/**
 * Resolved sports market — status + winning outcome; takes priority over is_live / extra
 * time. A sided fixture names its winner in `resolution`; one sold as a single outcome
 * names it on that outcome's `resolution_display` (the winning side's label).
 */
export const isSportsMarketResolved = question =>
  question?.status === QUESTION_STATUS.RESOLVED &&
  (question?.resolution != null || question?.outcomes?.some(outcome => outcome?.resolution_display != null))

export const showPenaltiesScore = sportsData => {
  if (!sportsData) return false
  const { home_score_penalties, away_score_penalties } = sportsData
  return home_score_penalties != null || away_score_penalties != null
}

export const hasExtraTimeScore = sportsData => {
  if (!sportsData) return false
  const { home_score_extra_time, away_score_extra_time } = sportsData
  return home_score_extra_time != null || away_score_extra_time != null
}

/**
 * Main displayed score: extra-time score during a penalty shootout,
 * regular-time score otherwise (extra time and penalties render separately).
 */
export const getMainSportsScores = sportsData => {
  if (showPenaltiesScore(sportsData)) {
    return { home: sportsData.home_score_extra_time, away: sportsData.away_score_extra_time }
  }
  return getRegularTimeScores(sportsData)
}

// Feed status codes (SportMonks-style) → i18n keys under question_card.sport_event
const MATCH_STATUS_LABEL_KEYS = {
  INPLAY_1ST_HALF: 'first_half',
  '1ST_HALF': 'first_half',
  HT: 'half_time',
  HALF_TIME: 'half_time',
  BREAK: 'half_time',
  INPLAY_2ND_HALF: 'second_half',
  '2ND_HALF': 'second_half',
  INPLAY_ET: 'extra_time',
  INPLAY_ET_2ND_HALF: 'extra_time',
  ET_BREAK: 'extra_time',
  EXTRA_TIME_BREAK: 'extra_time',
  AET: 'after_extra_time',
  INPLAY_PENALTIES: 'penalties',
  PEN_BREAK: 'penalties',
  PENALTIES: 'penalties',
  FT_PEN: 'penalties',
  FT: 'full_time',
}

/**
 * Human label for the live match phase. Decodes the coded feed status
 * (e.g. INPLAY_1ST_HALF → "1st half"); unknown codes are humanized
 * instead of shown raw.
 */
export const getMatchStatusLabel = sportsData => {
  const status = sportsData?.match_status || sportsData?.source_status
  const key = MATCH_STATUS_LABEL_KEYS[status?.toUpperCase?.()]
  if (key) return i18n(`question_card.sport_event.${key}`)

  if (status) {
    const humanized = status.replace(/_/g, ' ').toLowerCase()
    return humanized.charAt(0).toUpperCase() + humanized.slice(1)
  }

  return i18n('question_card.sport_event.live')
}

const SOCCER_EVENT_TYPE = 'soccer_match'

/** Soccer alone has the phase scores, minute clock and match-stats feed. */
export const isSoccerEvent = question => Boolean(question) && question.event_type === SOCCER_EVENT_TYPE

/**
 * Whether a market should render with the sports card UI (feed only): a fixture of any
 * sport — `soccer_match`, `basketball_match` (which the feed also uses for NFL, MLB, NHL
 * and MMA) — as long as it names its two sides.
 */
export const isSportsEvent = question => {
  if (!question) return false
  if (USE_DUMMY_DATA) return true
  if (isSoccerEvent(question)) return true
  const eventType = question.event_type
  if (typeof eventType !== 'string' || !eventType.endsWith('_match')) return false
  const sides = question.event_extra_data
  return Boolean(sides?.home_team && sides?.away_team)
}

/**
 * League name stored on the fixture (`event_extra_data.league`). Empty when the
 * feed did not send one. Callers render it on the sports chrome only.
 */
export const getSportsLeague = sportsData => {
  const league = sportsData?.league
  if (typeof league !== 'string') return null
  const trimmed = league.trim()
  return trimmed || null
}

/**
 * Sport per category slug. `icon` keys a drawing in SportIcon; `label` keys
 * `question_card.sport_event.sports.*`. The non-soccer feeds file each fixture under its
 * league's category (NBA, NFL, …) straight under Sports, so the slug names the sport.
 * A slug flagged `sport` names the sport itself rather than a league, so it is not
 * repeated as the league in the eyebrow.
 */
const SPORT_BY_CATEGORY_SLUG = {
  football: { icon: 'soccer', label: 'soccer', sport: true },
  soccer: { icon: 'soccer', label: 'soccer', sport: true },
  nba: { icon: 'basketball', label: 'basketball' },
  wnba: { icon: 'wnba', label: 'basketball' },
  nfl: { icon: 'american-football', label: 'american_football' },
  nhl: { icon: 'hockey', label: 'hockey' },
  mlb: { icon: 'baseball', label: 'baseball' },
  mma: { icon: 'boxing', label: 'mma', sport: true },
  boxing: { icon: 'boxing', label: 'boxing', sport: true },
  tennis: { icon: 'tennis', label: 'tennis', sport: true },
  f1: { icon: 'formula1', label: 'formula1', sport: true },
  'formula-1': { icon: 'formula1', label: 'formula1', sport: true },
  esports: { icon: 'esports', label: 'esports', sport: true },
}

/** The list endpoint sends the leaf category; the detail one sends the breadcrumb, root first. */
const getCategoryChain = question => {
  const category = question?.category
  if (Array.isArray(category)) return category.filter(Boolean)
  if (!category) return []
  return [category.parent, category].filter(c => c && typeof c === 'object')
}

/**
 * The fixture's sport and championship for the list row's eyebrow:
 * `{ icon, sportLabel, league }`, each null when unknown.
 */
export const getSportsKind = question => {
  const chain = getCategoryChain(question)
  const leaf = chain[chain.length - 1] ?? null

  let sport = isSoccerEvent(question) ? SPORT_BY_CATEGORY_SLUG.soccer : null
  for (let i = chain.length - 1; !sport && i >= 0; i--) {
    sport = SPORT_BY_CATEGORY_SLUG[chain[i]?.slug] ?? null
  }

  const leafIsSport = Boolean(leaf && SPORT_BY_CATEGORY_SLUG[leaf.slug]?.sport)
  const categoryLeague = leaf && leaf.slug !== 'sports' && !leafIsSport ? leaf.title?.trim() || null : null

  return {
    icon: sport?.icon ?? null,
    sportLabel: sport ? i18n(`question_card.sport_event.sports.${sport.label}`) : null,
    league: getSportsLeague(question?.event_extra_data) ?? categoryLeague,
  }
}

/**
 * Sports match data (teams, logos, score, status, kickoff, ...).
 */
export const getSportsEventData = question => {
  if (!isSportsEvent(question)) return null
  if (USE_DUMMY_DATA) {
    const { event_start_date, is_live, ...eventExtraData } = dummySportsEvent
    return eventExtraData
  }
  return question.event_extra_data ?? null
}

/**
 * Attaches dummy sports fields onto every question while USE_DUMMY_DATA is true.
 * No-op when using the live API.
 */
export const withSportsEventData = question => {
  if (!question || !USE_DUMMY_DATA) return question

  const { event_start_date, is_live, ...eventExtraData } = dummySportsEvent

  return {
    ...question,
    event_type: SOCCER_EVENT_TYPE,
    event_start_date,
    is_live,
    event_extra_data: eventExtraData,
  }
}

const MATCH_MINUTE_FIELDS = ['minute', 'match_minute', 'current_minute', 'live_minute', 'time_minute']
const ADDED_MINUTE_FIELDS = ['added_time', 'injury_time', 'extra_minute', 'added_minute']

/**
 * Elapsed match clock from the feed, e.g. "67'" or "45+2'".
 * Accepts numbers and the string forms livescore feeds send ("67", "67'", "45+2").
 * Returns null when the feed carries no minute (half-time, breaks, pre-match).
 */
const parseMinuteValue = value => {
  if (value == null || value === '') return null
  const match = String(value).match(/^\s*(\d+)(?:\s*\+\s*(\d+))?/)
  if (!match) return null
  return { minute: Number(match[1]), added: match[2] ? Number(match[2]) : null }
}

export const getMatchMinute = sportsData => {
  if (!sportsData) return null

  const parsed = MATCH_MINUTE_FIELDS.map(key => parseMinuteValue(sportsData[key])).find(Boolean)
  if (!parsed) return null

  const added =
    parsed.added ?? ADDED_MINUTE_FIELDS.map(key => parseMinuteValue(sportsData[key])).find(Boolean)?.minute ?? null

  return added > 0 ? `${parsed.minute}+${added}'` : `${parsed.minute}'`
}
